import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IonIcon } from '@ionic/react';
import { bagHandleOutline } from 'ionicons/icons';
import { useMyOrders } from '../../hooks/useOrders';
import { useAuthStore } from '../../store/useAuthStore';
import { useRootFontScale } from '../../hooks/useRootFontScale';
import type { MyOrderSummary, OrderStatus } from '../../types/order';
import { OrderStatusBreadcrumb } from './OrderStatusBreadcrumb';
import { formatPickupMoment, formatTimeWindow, presentWindow } from '../../utils/timeWindow';

// Post-payment, non-terminal statuses — the orders a buyer is still waiting on.
// Mirrors the lister panel's active buckets (paid/accepted/ready) plus the
// received variant. Pre-payment (held/confirmed) and terminal
// states (completed/canceled/abandoned/expired/no_show) drop off.
const IN_FLIGHT_STATUSES: OrderStatus[] = [
  'paid',
  'received',
  'accepted',
  'ready_to_pickup',
];

const isInFlight = (o: MyOrderSummary): boolean =>
  IN_FLIGHT_STATUSES.includes(o.status);

/** Timestamp (ms) of the buyer's intended pickup time, or +Infinity when unset
 * — so orders without a set time sort to the end rather than jumping to the
 * front. Mirrors the window-start sort in ActiveListingsOrdersPanel, but keyed
 * on the buyer's own chosen time (what we surface on each row). */
function pickupTimeMs(o: MyOrderSummary): number {
  const t = o.intendedPickupTime;
  return t ? new Date(t).getTime() : Number.POSITIVE_INFINITY;
}

/**
 * A buyer-facing summary that sits at the top of the home feed, mirroring the
 * chef-facing ActiveListingsOrdersPanel. One row per in-flight order — earliest
 * pickup window first — showing the dish, who it's from, and a live per-step
 * breadcrumb of where the order is. Renders nothing for anyone without an
 * in-flight order.
 */
export const ActiveOrdersPanel: React.FC = () => {
  const { t } = useTranslation('home');
  const history = useHistory();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // include='paid' (default) already hides never-paid reservations; we further
  // narrow to the in-flight subset below. First page (20) covers any realistic
  // number of concurrent orders, but we flatten all loaded pages anyway.
  const { data } = useMyOrders(false);

  // At large text sizes the side-by-side title/window columns squeeze the title
  // into a near-useless truncation — above this scale the window drops below the
  // title instead. Mirrors ActiveListingsOrdersPanel.
  const stackInfo = useRootFontScale() >= 1.15;

  const activeOrders = useMemo(() => {
    const all = data?.pages.flat() ?? [];
    if (!isAuthenticated) return [];
    return all
      .filter(isInFlight)
      .sort((a, b) => pickupTimeMs(a) - pickupTimeMs(b));
  }, [data, isAuthenticated]);

  if (activeOrders.length === 0) return null;

  return (
    <div className="border-b border-border">
      <ul className="divide-y divide-border">
        {activeOrders.map((order) => {
          const { listing } = order;
          const listerName =
            listing?.listerSummary?.businessName || listing?.listerSummary?.displayName;
          // The buyer's own chosen pickup time (not the listing's whole window),
          // resolved in the listing's zone and carrying its day. A row reading
          // just "Pickup 5:00pm" gave a buyer no way to tell tonight's order
          // from one three days out.
          const pickupMoment = formatPickupMoment(
            order.intendedPickupTime,
            listing?.timeWindow?.timezone,
          );
          // No `whitespace-nowrap`: with the day in it the label is long enough
          // to need a second line rather than push the row's layout.
          // Drop-off has no chosen time: it arrives inside the chef's window.
          const dropoffLabel =
            order.fulfillmentMethod === 'dropoff' && presentWindow(listing?.dropoffWindow)
              ? formatTimeWindow(listing!.dropoffWindow!)
              : '';
          const timeEl = dropoffLabel ? (
            <p className="text-right text-xs text-muted-foreground">
              {t('buyerOrders.dropoffAt', { time: dropoffLabel })}
            </p>
          ) : pickupMoment ? (
            <p className="text-right text-xs text-muted-foreground">
              {t('buyerOrders.pickupAt', { time: pickupMoment.full })}
            </p>
          ) : null;
          return (
            <li key={order.orderId}>
              <button
                type="button"
                onClick={() => history.push(`/orders/${order.orderId}`)}
                className="flex w-full flex-col gap-2 py-3 text-left active:opacity-80"
              >
                <div className={`flex gap-3 ${stackInfo ? 'items-start' : 'items-center'}`}>
                  {/* Thumbnail + role badge — the bag corner-badge marks this as
                      an order the buyer placed, distinguishing it from a
                      "your listing" row (chef-hat badge) when both panels stack. */}
                  <div className="relative flex-shrink-0">
                    {listing?.thumbnailUrl || listing?.imageUrl ? (
                      <img
                        src={listing.thumbnailUrl ?? listing.imageUrl ?? ''}
                        alt=""
                        className="h-11 w-11 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="h-11 w-11 rounded-lg bg-muted" />
                    )}
                    <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-brand-secondary">
                      <IonIcon icon={bagHandleOutline} className="text-[11px] text-white" />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {listing?.title ?? t('buyerOrders.unknownDish')}
                    </p>
                    {listerName && (
                      <p className="truncate text-xs text-muted-foreground">
                        {t('buyerOrders.from', { name: listerName })}
                      </p>
                    )}
                    {stackInfo && timeEl}
                  </div>
                  {!stackInfo && timeEl && (
                    <div className="flex-shrink-0">{timeEl}</div>
                  )}
                </div>
                <OrderStatusBreadcrumb status={order.status} fulfillmentMethod={order.fulfillmentMethod} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
